import * as React from 'react'

import { clsx } from 'clsx'

import { Flex, VisuallyHidden } from '@buffer-mono/popcorn'

import { PostMediaAsset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import type { CommentsPostCard_PostData } from '~publish/pages/Community/fragments/CommentsPostCard_Post'

import { BanIcon } from '../../../shared/BanIcon'
import sharedBadgeStyles from '../../../shared/CarouselBadge.module.css'
import { CommentMetrics } from '../CommentMetrics'

import styles from './PostGridItemCard.module.css'

import type { JSX } from 'react'

function UnsupportedPostMetrics(): JSX.Element {
  return (
    <Flex justify="end" align="center" fullWidth>
      <Flex
        justify="center"
        align="center"
        className={styles.unsupportedIndicator}
      >
        <BanIcon size={12} />
        <VisuallyHidden>Comments not available</VisuallyHidden>
      </Flex>
    </Flex>
  )
}

type PostGridItemCardProps = {
  post: CommentsPostCard_PostData
  isAllAddressed?: boolean
  supportsComments?: boolean
}

/**
 * Displays a post as a thumbnail card in grid view.
 *
 * Used in the Comments page sidepanel for visual-first platforms
 * (TikTok, YouTube, Instagram) where media thumbnails are more
 * useful than text previews.
 *
 * - Shows the first media asset as a thumbnail
 * - Displays a badge with remaining asset count for carousels (e.g., "+3")
 * - Shows comment metrics: progress bar + unread badge, revealing the
 *   interacted/total count on hover
 * - Renders empty placeholder for posts without media
 */
export function PostGridItemCard({
  post,
  isAllAddressed = false,
  supportsComments = true,
}: PostGridItemCardProps): JSX.Element {
  const [isHovered, setIsHovered] = React.useState(false)
  const hasAssets = Boolean(post.assets?.length)

  if (!hasAssets || !post.assets) {
    return (
      <div className={styles.emptyGridItem}>
        <div className={styles.emptyContent} />
      </div>
    )
  }

  const hasMultipleAssets = post.assets.length > 1
  const remainingCount = post.assets.length - 1

  return (
    <Flex
      direction="column"
      className={styles.gridItemWrapper}
      fullWidth
      onMouseEnter={(): void => setIsHovered(true)}
      onMouseLeave={(): void => setIsHovered(false)}
    >
      <div
        className={clsx(
          styles.gridItemContent,
          (isAllAddressed || !supportsComments) &&
            styles.gridItemContentAddressed,
        )}
      >
        <PostMediaAsset asset={post.assets[0]} className={styles.asset} />
        {hasMultipleAssets && (
          <span
            className={clsx(
              sharedBadgeStyles.carouselBadge,
              sharedBadgeStyles.medium,
            )}
          >
            +{remainingCount}
          </span>
        )}
      </div>
      <div className={styles.gridItemMetrics}>
        {supportsComments ? (
          <CommentMetrics
            postId={post.id}
            variant="grid"
            isActive={isHovered}
            className={styles.gridItemMetricsContent}
          />
        ) : (
          <UnsupportedPostMetrics />
        )}
      </div>
    </Flex>
  )
}
